/**
 * BridgeRenewPage — self-serve fresh link for an expired magic link.
 *
 * Reached from BridgeLanding when a tapped link is expired but renewable
 * (/bridge/renew?t=<old token>, fragment preserved). The patient types the
 * email address or mobile number the original message was sent to; the
 * bridge hash-compares it against the token's recipient_hash (it stores no
 * plaintext addresses) and returns a fresh 24-hour URL, which we open —
 * re-attaching the original URL fragment (the decryption key never leaves
 * the browser).
 */

import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import ParseoLogo from "../../shared/ui/ParseoLogo";

const BRIDGE_BASE_URL = (import.meta.env.VITE_BRIDGE_URL || "").replace(/\/+$/, "");

export default function BridgeRenewPage() {
  const [params] = useSearchParams();
  const token = params.get("t") || "";
  const [recipient, setRecipient] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function requestNewLink(e?: React.FormEvent<HTMLFormElement>) {
    e?.preventDefault();
    if (!recipient.trim() || busy) return;
    setBusy(true);
    setError("");
    try {
      const resp = await fetch(`${BRIDGE_BASE_URL}/api/bridge/magic/renew`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ t: token, recipient: recipient.trim() }),
      });
      if (!resp.ok) {
        setError(
          resp.status === 401
            ? "That doesn't match the address this link was sent to. Check for typos, or contact your provider's office for a new link."
            : resp.status === 410
            ? "This form's link lapsed 7 days after it was first sent and can no longer be renewed here — please contact your clinician's office for a new link."
            : "Something went wrong — please try again."
        );
        setBusy(false);
        return;
      }
      const data = await resp.json();
      const fragment = window.location.hash || "";
      window.location.href = `${data.url}${fragment}`;
    } catch {
      setError("Something went wrong — please try again.");
      setBusy(false);
    }
  }

  return (
    <div style={{
      minHeight: "100vh", display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      backgroundColor: "#f5f0eb", padding: 24, textAlign: "center",
    }}>
      <ParseoLogo size={48} />
      <h2 style={{ color: "#182a33", margin: "16px 0 6px" }}>Get a fresh link</h2>
      <p style={{ color: "#5b6e7c", maxWidth: 420, lineHeight: 1.5, margin: "0 0 18px" }}>
        That link has expired. Enter the email address or mobile number your
        link was sent to and we'll open a new one right here.
      </p>
      <form onSubmit={requestNewLink} style={{ width: "100%", maxWidth: 360 }}>
        <input
          type="text"
          value={recipient}
          onChange={(e) => setRecipient(e.target.value)}
          placeholder="Email or mobile number"
          autoFocus
          style={{
            width: "100%", boxSizing: "border-box", padding: "12px 14px",
            fontSize: 15, borderRadius: 10, border: "1px solid #d8c9b6",
            outline: "none", textAlign: "center",
          }}
        />
        {error && (
          <p style={{ color: "#c25450", fontSize: 13, lineHeight: 1.45, margin: "10px 0 0" }}>
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={busy || !recipient.trim()}
          style={{
            marginTop: 14, width: "100%", padding: "12px 20px", borderRadius: 10,
            border: "none", background: "#1b5e6e", color: "#fff",
            fontSize: 15, fontWeight: 600,
            cursor: busy || !recipient.trim() ? "default" : "pointer",
            opacity: busy || !recipient.trim() ? 0.6 : 1,
          }}
        >
          {busy ? "Checking…" : "Open my new link"}
        </button>
      </form>
    </div>
  );
}
